import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Play, Search, X } from "lucide-react";
import { PATREON_URL, CLUB_PERKS, JOIN_URL } from "@/lib/club";
import { WORDS, FAVES, BRANDS } from "./concept-content";
import { nextClasses } from "./ClubRichThread";
import { useVideoCards, useMenu, FOLLOW, footerLinks, Signup } from "./ConceptBits";
import "./HomeRound5.css";
import "./HomePattern.css";

/* Concept from the "Color outside the pattern" mockup (1672 × 941). The
   mockup's model isn't Chelsea, so the arch holds her real cutout. Torn paper
   is generated SVG; the two polaroid photos are cropped from the mockup. */

export const EASE = [0.22, 1, 0.36, 1] as const;
const CLUB = "/club-preview/pattern";
const HOME = "/home-preview/pattern";

/* The right-hand scene is its own frame (mockup x 600–1672), so on phones it
   can be shown on its own and cropped to the arch. */
const S = { x: 600, w: 1072, h: 941 };
const sbox = (x: number, y: number, w?: number, h?: number): CSSProperties => ({
  left: `${((x - S.x) / S.w) * 100}%`, top: `${(y / S.h) * 100}%`,
  ...(w !== undefined && { width: `${(w / S.w) * 100}%` }),
  ...(h !== undefined && { height: `${(h / S.h) * 100}%` }),
});

/* ─── Torn paper ─── */

export function rng(seed: number) {
  return () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
}

/** Jagged points along a polyline, jittered perpendicular to each segment. */
function tear(line: [number, number][], seed: number, amp = 5, step = 9) {
  const r = rng(seed), out: [number, number][] = [];
  for (let i = 0; i < line.length - 1; i++) {
    const [x1, y1] = line[i], [x2, y2] = line[i + 1];
    const len = Math.hypot(x2 - x1, y2 - y1), n = Math.max(1, Math.round(len / step));
    const nx = -(y2 - y1) / len, ny = (x2 - x1) / len;
    for (let k = 0; k < n; k++) {
      const t = k / n, j = (r() - 0.5) * 2 * amp * (r() < 0.15 ? 2 : 1);
      out.push([x1 + (x2 - x1) * t + nx * j, y1 + (y2 - y1) * t + ny * j]);
    }
  }
  out.push(line[line.length - 1]);
  return out;
}

/** A torn sheet: colored paper over a slightly larger white "fiber" edge. */
export function Torn({ edge, close, fill, seed, fiber = 6, jag = 1 }: { edge: [number, number][]; close: [number, number][]; fill: string; seed: number; fiber?: number; jag?: number }) {
  const pts = (shift: number, s: number) => {
    const e = tear(edge, s, (shift ? 6 : 4) * jag);
    // Push the fiber layer outward by sampling the edge's normal.
    const out = e.map(([x, y], i) => {
      const [ax, ay] = e[Math.max(0, i - 1)], [bx, by] = e[Math.min(e.length - 1, i + 1)];
      const len = Math.hypot(bx - ax, by - ay) || 1;
      return [x - ((by - ay) / len) * shift, y + ((bx - ax) / len) * shift] as [number, number];
    });
    return [...out, ...close].map(p => p.join(",")).join(" ");
  };
  return <>
    <polygon points={pts(fiber, seed + 7)} fill="#fbf8f2" style={{ filter: "drop-shadow(0 2px 3px #0000001f)" }} />
    <polygon points={pts(0, seed)} fill={fill} />
  </>;
}

/* ─── Bits ─── */

export function useFonts() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600..900;1,600..900&family=Nothing+You+Could+Do&family=Mrs+Saint+Delafield&family=Figtree:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
}

export function Draw({ d, delay = 0, width = 3, dash }: { d: string; delay?: number; width?: number; dash?: boolean }) {
  if (dash) return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeDasharray="9 9"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay, duration: 0.8 }} />;
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
    transition={{ pathLength: { duration: 0.55, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

function Note({ children, className, style, delay }: { children: ReactNode; className: string; style?: CSSProperties; delay: number }) {
  return <motion.div className={`hp-abs ${className}`} style={style} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay, duration: 0.5 }}>{children}</motion.div>;
}

export const Heart = ({ delay = 0 }: { delay?: number }) => <svg className="hp-heart" viewBox="0 0 30 28" aria-hidden="true">
  <Draw d="M15 25 C 5 18, 2 12, 4 7 C 6 2, 13 2, 15 8 C 17 2, 24 2, 26 7 C 28 12, 25 18, 15 25 Z" delay={delay} width={2.2} />
</svg>;

/* ─── Hero ─── */

const MENU = [
  { to: HOME, label: "Home", tone: "blue" },
  { to: "/about", label: "About", tone: "pink" },
  { to: "/videos", label: "Projects", tone: "ink" },
  { to: CLUB, label: "The club", tone: "blue" },
  { to: "/guides", label: "Journal", tone: "pink" },
  { to: "/shop", label: "Shop", tone: "ink" },
] as const;

/** Phone menu: a cream sheet that drops down, with the hero's torn blue paper at its foot. */
function Menu({ menu }: { menu: ReturnType<typeof useMenu> }) {
  return <AnimatePresence>
    {menu.open && <motion.div id="hp-menu" ref={menu.panel} className="hp-menu" role="dialog" aria-modal="true" aria-label="Menu"
      initial={{ y: "-100%" }} animate={{ y: 0 }} exit={{ y: "-100%" }} transition={{ duration: 0.5, ease: EASE }}>
      <svg className="hp-menu-paper" viewBox="0 0 400 220" preserveAspectRatio="none" aria-hidden="true">
        <Torn seed={5} fill="#1d3fc8" fiber={5} edge={[[400, 70], [300, 92], [190, 118], [90, 150], [0, 176]]} close={[[0, 220], [400, 220]]} />
      </svg>
      <div className="hp-menu-top">
        <Link to={HOME} className="hp-logo">A Few<br />Loose Threads</Link>
        <button type="button" className="hp-burger" onClick={() => menu.setOpen(false)} aria-label="Close menu"><X size={22} aria-hidden="true" /></button>
      </div>
      <nav aria-label="Main">
        <ol>{MENU.map((m, i) => <motion.li key={m.label} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.05, duration: 0.45, ease: EASE }}>
          <span>0{i + 1}</span><Link to={m.to} className={`hp-menu-${m.tone}`}>{m.label}</Link>
        </motion.li>)}</ol>
      </nav>
      <motion.div className="hp-menu-foot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        <Link to={CLUB} className="hp-cta">Let’s sew <ArrowRight size={18} aria-hidden="true" /></Link>
        <p className="hp-pen">Creative people,<br />happier people <Heart delay={0.9} /></p>
      </motion.div>
    </motion.div>}
  </AnimatePresence>;
}

/** On the club page the club link replaces Journal and the button goes straight to checkout. */
export function Nav({ club = false }: { club?: boolean }) {
  const menu = useMenu();
  return <header className="hp-nav">
    <Link to={HOME} className="hp-logo">A Few<br />Loose Threads</Link>
    <nav aria-label="Main">
      <Link to={HOME} aria-current={club ? undefined : "page"}>Home</Link>
      <Link to="/about">About</Link>
      <Link to="/videos">Projects</Link>
      {club ? <Link to={CLUB} aria-current="page">The club</Link> : <Link to="/guides">Journal</Link>}
      <Link to="/shop">Shop</Link>
    </nav>
    <Link to="/guides" className="hp-search" aria-label="Search"><Search size={22} /></Link>
    {club
      ? <a href={JOIN_URL} className="hp-cta"><span>Join<span className="hp-cta-more"> the club</span></span> <ArrowRight size={18} aria-hidden="true" /></a>
      : <Link to={CLUB} className="hp-cta">Let’s sew <ArrowRight size={18} aria-hidden="true" /></Link>}
    <button ref={menu.toggle} type="button" className="hp-burger" aria-label="Open menu" aria-expanded={menu.open} aria-controls="hp-menu" onClick={() => menu.setOpen(true)}>
      <svg viewBox="0 0 24 18" aria-hidden="true"><path d="M2 3 H22" /><path d="M2 9 H16" /><path d="M2 15 H22" /></svg>
    </button>
    <Menu menu={menu} />
  </header>;
}

const POLAROIDS = [
  { src: "/concept/gingham-top.jpg", alt: "A blue gingham top with puff sleeves", at: [1186, 246, 318, 326], r: 4, caption: <>Handmade<br />hits different.</> },
  { src: "/concept/label.jpg", alt: "A sewn-in label that says Good Outfits Brighter Days", at: [1380, 512, 270, 372], r: 7, caption: <>Details<br />matter.</> },
] as const;

function Scene() {
  return <div className="hp-scene">
    <svg className="hp-paper" viewBox={`${S.x} 0 ${S.w} ${S.h}`} preserveAspectRatio="none" aria-hidden="true">
      <motion.path d="M685,880 V305 A288,288 0 0 1 1261,305 V880 Z" fill="#f7a1c4" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: EASE }} style={{ transformOrigin: "973px 880px" }} />
      <Torn seed={11} fill="#1d3fc8" edge={[[1206, 0], [1214, 52], [1252, 108], [1330, 138], [1428, 150], [1560, 118], [1672, 88]]} close={[[1672, 0]]} />
      <Torn seed={23} fill="#f6d64a" edge={[[1672, 112], [1560, 140], [1440, 176], [1404, 250], [1392, 420], [1380, 600], [1404, 780], [1430, 941]]} close={[[1672, 941]]} />
      <Torn seed={37} fill="#fbf7f0" edge={[[668, 870], [820, 840], [1000, 790], [1180, 778], [1320, 792], [1440, 812]]} close={[[1440, 941], [668, 941]]} />
    </svg>
    <svg className="hp-dashes" viewBox={`${S.x} 0 ${S.w} ${S.h}`} preserveAspectRatio="none" aria-hidden="true">
      <Draw dash d="M1196,0 C1180,40 1188,92 1236,118" delay={1.2} width={2.6} />
      <Draw dash d="M1468,388 C1520,430 1534,500 1504,570" delay={1.3} width={2.6} />
      <Draw dash d="M1390,770 C1320,700 1220,620 1140,640 C1080,660 1090,740 1170,760 C1260,780 1330,720 1350,640 C1360,600 1352,560 1345,540" delay={1.4} width={2.6} />
    </svg>

    <motion.img className="hp-abs hp-chelsea" src="/chelsea-cutout-trim.webp" alt="Chelsea smiling with one arm raised, in a straw hat and a colorful checked sundress"
      style={sbox(712, 76, 309, 846)} initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7, ease: EASE }} />

    <svg className="hp-abs hp-burst" style={sbox(1000, 62, 60, 76)} viewBox="0 0 60 76" aria-hidden="true">
      <Draw d="M14 40 L30 6" delay={1.0} width={3.6} /><Draw d="M22 52 L50 34" delay={1.08} width={3.6} /><Draw d="M28 68 L56 70" delay={1.16} width={3.6} />
    </svg>

    <Note className="hp-script hp-same" style={sbox(1046, 250)} delay={1.3}>
      <span>Same</span><span>girl....</span><span>more</span><span>fabric</span><span>please.</span><Heart delay={1.8} />
    </Note>

    <motion.div className="hp-abs hp-card" style={sbox(1160, 70, 270, 248)} initial={{ opacity: 0, y: -20, rotate: -12 }} animate={{ opacity: 1, y: 0, rotate: -6 }} transition={{ delay: 0.7, type: "spring", stiffness: 150, damping: 15 }}>
      <ul aria-label="The plan">
        {["Sew things", "Be inspired", "Wear the joy", "Repeat"].map((t, i) => <li key={t}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="2" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" />{i === 0 && <Draw d="M6 12 L10 17 L20 3" delay={1.4} width={2.6} />}</svg>
          {t}{i === 3 && <Heart delay={1.6} />}
        </li>)}
      </ul>
    </motion.div>

    {POLAROIDS.map((p, i) => <motion.figure key={p.src} className="hp-abs hp-polaroid" style={sbox(p.at[0], p.at[1], p.at[2], p.at[3])}
      initial={{ opacity: 0, y: 40, rotate: p.r + 10 }} animate={{ opacity: 1, y: 0, rotate: p.r }} whileHover={{ y: -6, rotate: p.r / 2 }}
      transition={{ delay: 0.85 + i * 0.15, type: "spring", stiffness: 140, damping: 15 }}>
      <span className="hp-polaroid-img"><img src={p.src} alt={p.alt} /></span>
      <figcaption>{p.caption}<Heart delay={1.5 + i * 0.2} /></figcaption>
    </motion.figure>)}

    <Note className="hp-caps" style={sbox(1540, 286)} delay={1.5}>Bolder<br />Brighter<br />Happier<br />Handmade<Heart delay={1.9} /></Note>

    <Note className="hp-sig" style={sbox(1054, 786)} delay={1.6}>
      <p className="hp-sig-name">Chelsea <Heart delay={2.1} /></p>
      <p className="hp-sig-role">Founder, A Few Loose Threads</p>
    </Note>
  </div>;
}

function Hero() {
  return <section className="hp-hero" aria-labelledby="hp-title">
    <div className="hp-stage">
      <svg className="hp-left-paper" viewBox="0 0 1672 941" preserveAspectRatio="none" aria-hidden="true">
        <Torn seed={5} fill="#1d3fc8" edge={[[0, 752], [150, 768], [310, 784], [470, 806], [600, 868], [700, 941]]} close={[[0, 941]]} />
      </svg>
      <svg className="hp-left-dash" viewBox="0 0 1672 941" preserveAspectRatio="none" aria-hidden="true">
        <Draw dash d="M0,912 C60,900 100,870 112,840 C130,800 90,792 100,822 C 80,790 60,810 84,850 C110,890 200,880 240,840 C 270,810 250,770 210,800" delay={1.5} width={2.4} />
      </svg>
      <Nav />
      <div className="hp-copy">
        <h1 id="hp-title">
          {["Color", "outside", "the pattern."].map((w, i) => <motion.span key={w} className={`hp-h-${i}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.12, duration: 0.6, ease: EASE }}>{w}</motion.span>)}
          <svg className="hp-underline" viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true"><Draw d="M4 12 C 80 6, 200 6, 296 9" delay={0.8} width={7} /></svg>
        </h1>
        <motion.div className="hp-copy-rest" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }}>
          <p className="hp-sub">Sewing, style, and a more colorful life.</p>
          <p className="hp-lede">A Few Loose Threads is where modern sewing meets real life: creative projects, personal style, and a little encouragement to make it your own.</p>
          <div className="hp-actions">
            <a href="#making" className="hp-btn hp-btn-blue">Explore projects <ArrowRight size={18} aria-hidden="true" /></a>
            <Link to="/about" className="hp-btn hp-btn-line">Meet Chelsea</Link>
          </div>
        </motion.div>
      </div>
      <Note className="hp-script hp-creative" style={{ left: "21%", top: "88%" }} delay={1.7}>
        <span>Creative</span><span>People</span><span>Happier</span><span>People <Heart delay={2.2} /></span>
      </Note>
      <Scene />
    </div>
  </section>;
}

/* ─── Sections ─── */

export const VIEW = { once: true, margin: "-80px" } as const;
const DENVER = "America/Denver";
export const datePart = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleDateString("en-US", { timeZone: DENVER, ...o });

/** A pen stroke that draws itself when scrolled into view. */
export function Ink({ d, delay = 0, width = 3 }: { d: string; delay?: number; width?: number }) {
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={VIEW}
    transition={{ pathLength: { duration: 0.6, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

export const InkHeart = ({ delay = 0 }: { delay?: number }) => <svg className="hp-heart" viewBox="0 0 30 28" aria-hidden="true">
  <Ink d="M15 25 C 5 18, 2 12, 4 7 C 6 2, 13 2, 15 8 C 17 2, 24 2, 26 7 C 28 12, 25 18, 15 25 Z" delay={delay} width={2.2} />
</svg>;

/** A word with the hero's yellow marker underline. */
export function U({ children }: { children: ReactNode }) {
  return <span className="hp-u">{children}<svg viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true"><Ink d="M4 12 C 80 6, 200 6, 296 9" delay={0.5} width={7} /></svg></span>;
}

export function Head({ n, kicker, id, children, note, className = "" }: { n: string; kicker: string; id: string; children: ReactNode; note?: ReactNode; className?: string }) {
  return <motion.header className={`hp-head ${className}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
    <p className="hp-kicker"><span>{n}</span>{kicker}</p>
    <h2 id={id}>{children}</h2>
    {note && <p className="hp-pen hp-head-note">{note}</p>}
  </motion.header>;
}

function useWidth<T extends Element>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(1600);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(100, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** A torn edge for a full-width band, drawn in real pixels so the tears keep their size on any screen. */
export function TornEdge({ fill, seed, side }: { fill: string; seed: number; side: "top" | "bottom" }) {
  const [ref, w] = useWidth<SVGSVGElement>();
  const H = 30, r = rng(seed), line: [number, number][] = [];
  for (let x = 0; x < w; x += 140) line.push([x, 9 + r() * 12]);
  line.push([w, 9 + r() * 12]);
  // The fiber layer sits on the left of the drawing direction, so draw toward the paper's outside.
  const edge = side === "top" ? [...line].reverse() : line;
  const close: [number, number][] = side === "top" ? [[0, H + 2], [w, H + 2]] : [[w, -2], [0, -2]];
  return <svg ref={ref} className={`hp-edge hp-edge-${side}`} viewBox={`0 0 ${w} ${H}`} preserveAspectRatio="none" aria-hidden="true">
    <Torn seed={seed} fill={fill} edge={edge} close={close} fiber={4} jag={0.55} />
  </svg>;
}

export function Band({ color, seed, className, children, top = true, bottom = true, id, label }: { color: string; seed: number; className: string; children: ReactNode; top?: boolean; bottom?: boolean; id?: string; label: string }) {
  return <section id={id} className={`hp-band ${className}`} style={{ background: color }} aria-labelledby={label}>
    {top && <TornEdge fill={color} seed={seed} side="top" />}
    {children}
    {bottom && <TornEdge fill={color} seed={seed + 50} side="bottom" />}
  </section>;
}

const ARCH = ["pink", "blue", "yellow"] as const;

function Making() {
  const cards = useVideoCards(3);
  return <section id="making" className="hp-sec hp-making" aria-labelledby="hp-making-title">
    <div className="hp-wrap">
      <div className="hp-row">
        <Head n="01" kicker="Fresh off the machine" id="hp-making-title">What I’m <span className="hp-pink">making</span> <U>now.</U></Head>
        <Link to="/videos" className="hp-btn hp-btn-line hp-row-btn">All videos <ArrowRight size={18} aria-hidden="true" /></Link>
      </div>

      <div className="hp-making-grid">
        <svg className="hp-making-thread" viewBox="0 0 1000 200" preserveAspectRatio="none" aria-hidden="true"><path d="M-10 150 C 120 40, 230 40, 330 120 S 560 200, 680 90 S 900 20, 1010 110" /></svg>
        {cards.map((c, i) => <motion.a key={c.id} className="hp-arch-card" href={c.url} target="_blank" rel="noreferrer"
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * 0.12, duration: 0.6, ease: EASE }}>
          <span className={`hp-arch hp-arch-${ARCH[i]}`}><span><img src={c.img} alt="" style={{ objectPosition: c.pos }} /></span><b><Play size={18} fill="currentColor" aria-hidden="true" /></b></span>
          <span className="hp-tag">{c.tag}</span>
          <span className="hp-arch-title">{c.title}</span>
          <span className="hp-pen hp-arch-go">watch it <ArrowRight size={16} aria-hidden="true" /></span>
        </motion.a>)}

        <motion.div className="hp-follow" initial={{ opacity: 0, y: 30, rotate: 2 }} whileInView={{ opacity: 1, y: 0, rotate: -3 }} viewport={VIEW} transition={{ delay: 0.4, type: "spring", stiffness: 120, damping: 15 }}>
          <figure className="hp-follow-photo">
            <img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" />
            <figcaption className="hp-pen">Cosmo, quality control</figcaption>
          </figure>
          <p className="hp-pen hp-follow-title">Follow along <InkHeart delay={0.8} /></p>
          <ul>{FOLLOW.map(f => <li key={f.s.key}>
            <a href={f.s.url} target="_blank" rel="noreferrer">
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="2" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" /><path className="hp-tick" d="M6 12 L10 17 L20 3" /></svg>
              <span>{f.s.platform}</span><small>{f.s.handle}</small>
            </a>
          </li>)}</ul>
        </motion.div>
      </div>
    </div>
  </section>;
}

const YARDAGE = [["Live classes", "2 a month"], ["Replays", "Every one"], ["Skill level", "Any"], ["Sizes", "Everybody"]];

function Club() {
  const dates = nextClasses(2);
  return <Band id="club" color="#1d3fc8" seed={61} className="hp-club" label="hp-club-title">
    <div className="hp-wrap hp-club-grid">
      <div className="hp-club-copy">
        <Head n="02" kicker="Loose Threads Club · my Patreon" id="hp-club-title">Sew with me, <span className="hp-yellow">twice a month.</span></Head>
        <p className="hp-club-lede">Every month we pick one bigger project and I teach it live, twice. Ask questions, tackle the tricky parts together, and sew next to people who get it.</p>
        <p className="hp-club-next"><span>Next classes</span>{dates.map(d => datePart(d, { weekday: "short", month: "short", day: "numeric" })).join("  ·  ")} · 7 PM MT</p>
        <div className="hp-actions hp-club-actions">
          <a href={PATREON_URL} target="_blank" rel="noreferrer" className="hp-btn hp-btn-yellow">Join the club <ArrowRight size={18} aria-hidden="true" /></a>
          <Link to={CLUB} className="hp-btn hp-btn-ghost">See what’s inside</Link>
        </div>
        <p className="hp-pen hp-club-pen">Can’t make it live? Every class is recorded. <InkHeart delay={1} /></p>
        <p className="hp-club-foot">Just want to cheer me on? <a href={PATREON_URL} target="_blank" rel="noreferrer">Become a Thread Friend for $3/month.</a></p>
      </div>

      <div className="hp-env-wrap">
        <motion.div className="hp-tissue" aria-hidden="true" initial={{ opacity: 0, rotate: 0 }} whileInView={{ opacity: 1, rotate: 7 }} viewport={VIEW} transition={{ delay: 0.3, duration: 0.8, ease: EASE }}>
          <svg viewBox="0 0 300 400" preserveAspectRatio="none"><path d="M30 30 L250 18 L274 200 L240 380 L40 372 L20 200 Z" /><path className="hp-grain" d="M150 90 V310 M136 108 L150 90 L164 108 M136 292 L150 310 L164 292" /></svg>
          <span>Piece A · Cut 2 · Fold</span>
        </motion.div>
        <motion.article className="hp-env" aria-label="Loose Threads Club, $25 a month" initial={{ opacity: 0, y: 40, rotate: 2 }} whileInView={{ opacity: 1, y: 0, rotate: -3 }} viewport={VIEW} transition={{ type: "spring", stiffness: 110, damping: 16 }}>
          <header className="hp-env-top">
            <b>LTC</b>
            <span><small>Pattern no. 025</small>Loose Threads Club</span>
            <em>Sew-along<br />all levels</em>
          </header>
          <div className="hp-env-body">
            <figure className="hp-env-photo">
              <span><img src="/chelsea-rainbow-portrait.jpg" alt="Chelsea smiling and waving during a live class" /></span>
              <i className="hp-env-live"><b />Live</i>
              <figcaption className="hp-pen">Tuesday nights <InkHeart delay={1.2} /></figcaption>
            </figure>
            <div className="hp-env-info">
              <p className="hp-env-price"><b>$25</b>/month</p>
              <p className="hp-env-label">Pattern includes</p>
              <ol>{CLUB_PERKS.map((p, i) => <li key={p.title}><span>{"ABCD"[i]}</span>{p.title}</li>)}</ol>
            </div>
          </div>
          <dl className="hp-env-yard">{YARDAGE.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </motion.article>
      </div>
    </div>
  </Band>;
}

const LABELS = ["white", "yellow", "blue"] as const;

function KindWords() {
  return <Band color="#f7a1c4" seed={83} className="hp-words" label="hp-words-title" top={false}>
    <div className="hp-wrap">
      <Head n="03" kicker="Kind words" id="hp-words-title" className="hp-head-center">People who sew <span className="hp-blue">with me.</span></Head>
      <div className="hp-words-grid">
        {WORDS.map((w, i) => <motion.figure key={w.name} className={`hp-label hp-label-${LABELS[i]}`}
          initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: [-3, 2, -1.5][i] }} viewport={VIEW} transition={{ delay: i * 0.12, duration: 0.6, ease: EASE }}>
          <blockquote className="hp-pen">“{w.quote}”</blockquote>
          <figcaption><b>{w.name}</b>{w.tag}</figcaption>
        </motion.figure>)}
      </div>
    </div>
  </Band>;
}

function Favorites() {
  const [on, setOn] = useState(0);
  return <section id="favorites" className="hp-sec hp-faves" aria-labelledby="hp-faves-title">
    <div className="hp-wrap">
      <div className="hp-row">
        <Head n="04" kicker="Things I use and love" id="hp-faves-title" note={<>tools I’d actually buy again <InkHeart delay={0.9} /></>}>My <span className="hp-blue">favorite</span> things.</Head>
        <Link to="/favorites" className="hp-btn hp-btn-line hp-row-btn">See all my favorites <ArrowRight size={18} aria-hidden="true" /></Link>
      </div>
      <div className="hp-faves-grid">
        <div className="hp-faves-arch" aria-hidden="true">
          <svg viewBox="0 0 480 560" preserveAspectRatio="none"><path d="M0 560 V240 A240 240 0 0 1 480 240 V560 Z" /></svg>
          <AnimatePresence mode="popLayout">
            <motion.img key={FAVES[on].img} src={FAVES[on].img} alt="" initial={{ opacity: 0, scale: 0.94, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 2 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.4, ease: EASE }} />
          </AnimatePresence>
          <p className="hp-caps hp-faves-caps">Tried<br />Tested<br />Loved</p>
        </div>
        <motion.div className="hp-list" initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: -1.5 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
          <p className="hp-pen hp-list-title">Shopping list</p>
          <ol>{FAVES.map((f, i) => <li key={f.name}>
            <Link to="/favorites" className={i === on ? "is-on" : ""} onMouseEnter={() => setOn(i)} onFocus={() => setOn(i)}>
              <span className="hp-list-no">No. 0{i + 1}</span>
              <span className="hp-list-thumb"><img src={f.img} alt="" /></span>
              <span className="hp-list-text"><span className="hp-tag">{f.tag}</span><b>{f.name}</b><span className="hp-pen">{f.why}</span></span>
              <ArrowUpRight size={22} aria-hidden="true" />
            </Link>
          </li>)}</ol>
        </motion.div>
      </div>
      <p className="hp-disclose">Some links are affiliate links. If you buy through them, I may earn a small commission at no extra cost to you.</p>
    </div>
  </section>;
}

function WhereIllBe() {
  const [next] = nextClasses(1);
  const rows = [
    { to: "/events/stitches-night-out", m: "Oct", d: "20", what: "Stitches Night Out", where: "American Fork, UT", note: "a sewists’ movie night!" },
    { to: CLUB, m: datePart(next, { month: "short" }), d: datePart(next, { day: "numeric" }), what: "Live class · Loose Threads Club", where: "Online · 7 PM MT", note: "bring your machine" },
    { to: "/retreats", m: "Soon", d: "✿", what: "Sewing retreats", where: "In person · a few days away", note: "sew, eat, laugh, repeat" },
  ];
  return <Band color="#f6d64a" seed={97} className="hp-where" label="hp-where-title">
    <div className="hp-wrap hp-where-grid">
      <div>
        <Head n="05" kicker="Come say hi" id="hp-where-title">Where I’ll <span className="hp-blue">be.</span></Head>
        <ul className="hp-rows">
          {rows.map((r, i) => <motion.li key={r.what} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={VIEW} transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }}>
            <Link to={r.to}>
              <span className="hp-row-date"><small>{r.m}</small>{r.d}</span>
              <span className="hp-row-what"><b>{r.what}</b><span>{r.where}</span></span>
              <span className="hp-pen hp-row-note">{r.note}</span>
              <span className="hp-row-go"><ArrowRight size={20} aria-hidden="true" /></span>
            </Link>
          </motion.li>)}
        </ul>
      </div>
      <motion.figure className="hp-polaroid hp-where-photo" initial={{ opacity: 0, y: 40, rotate: 10 }} whileInView={{ opacity: 1, y: 0, rotate: 5 }} viewport={VIEW} transition={{ delay: 0.3, type: "spring", stiffness: 120, damping: 15 }}>
        <i className="hp-tape" aria-hidden="true" />
        <span className="hp-polaroid-img"><img src="/events/practical-magic-2.jpg" alt="Practical Magic movie poster for Stitches Night Out" /></span>
        <figcaption className="hp-pen">movie night!! <InkHeart delay={1} /></figcaption>
      </motion.figure>
    </div>
  </Band>;
}

function Brands() {
  return <section className="hp-brands" aria-labelledby="hp-brands-title">
    <h2 id="hp-brands-title" className="hp-kicker">Brands &amp; friends I’ve worked with</h2>
    <ul>{BRANDS.map(b => <li key={b.name}><img src={b.src} alt={b.name} /></li>)}</ul>
    <Link to="/about" className="hp-textlink">Work with me <ArrowRight size={15} aria-hidden="true" /></Link>
  </section>;
}

function Loop() {
  return <section id="newsletter" className="hp-sec hp-loop" aria-labelledby="hp-loop-title">
    <svg className="hp-loop-paper" viewBox="0 0 1600 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <Torn seed={101} fill="#1d3fc8" edge={[[0, 430], [120, 470], [230, 560], [290, 700]]} close={[[0, 700]]} />
      <Torn seed={113} fill="#f6d64a" edge={[[1600, 120], [1500, 110], [1400, 60], [1350, 0]]} close={[[1600, 0]]} />
    </svg>
    <svg className="hp-loop-dash" viewBox="0 0 1600 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <path d="M40 300 C 120 250, 220 300, 260 380 S 300 520, 420 560" />
      <path d="M1580 300 C 1500 340, 1440 300, 1400 240 S 1300 150, 1200 170" />
    </svg>
    <motion.div className="hp-loop-arch" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.7, ease: EASE }}>
      <p className="hp-kicker"><span>06</span>Letters from the sewing room</p>
      <h2 id="hp-loop-title">One email a week. <span className="hp-blue">Mostly fabric.</span></h2>
      <p className="hp-loop-lede">What I’m making, a helpful tip, and first dibs on retreats and events.</p>
      <Signup source="home-pattern" className="hp-signup" button={<>Sign me up <ArrowRight size={18} aria-hidden="true" /></>} done={<>You’re in. See you in your inbox! <span aria-hidden="true">♡</span></>} />
      <p className="hp-sig-name hp-loop-sig">Chelsea</p>
    </motion.div>
  </section>;
}

export function Footer() {
  return <footer className="hp-foot">
    <TornEdge fill="#1d3fc8" seed={131} side="top" />
    <div className="hp-wrap hp-foot-grid">
      <div>
        <Link to="/" className="hp-logo hp-foot-logo">A Few<br />Loose Threads</Link>
        <p className="hp-pen hp-foot-pen">Color outside the pattern. <InkHeart delay={0.6} /></p>
      </div>
      <nav aria-label="Footer"><ul>{footerLinks(CLUB).map(l => <li key={l.href}><Link to={l.href}>{l.label}</Link></li>)}</ul></nav>
      <div>
        <p className="hp-kicker">Follow along</p>
        <ul className="hp-foot-social">{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
      </div>
    </div>
    <p className="hp-foot-fine">© {new Date().getFullYear()} A Few Loose Threads · Bolder, brighter, happier, handmade.</p>
  </footer>;
}

export function HomePattern() {
  useFonts();
  return <div className="h5 h5-pal-pattern hp">
    <Hero />
    <Making />
    <Club />
    <KindWords />
    <Favorites />
    <WhereIllBe />
    <Brands />
    <Loop />
    <Footer />
  </div>;
}
